import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Building2, Wallet, CheckCircle2, ArrowRight, Landmark, Factory, Home, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Services = () => {
  const services = [
    {
      id: 'subsidies',
      icon: <FileText className="w-10 h-10" />,
      title: 'Government Subsidies',
      subtitle: 'State & Central Government Incentives',
      description: 'A subsidy or government incentive is a form of financial aid extended to businesses to generate employment and promote economic growth. We offer single-window solutions that add unique value to your business with customized approaches for MSMEs.',
      features: [
        'Capital Subsidy schemes',
        'Interest Subsidy programs',
        'State Government incentives',
        'Central Government schemes',
        'MSME-specific benefits',
        'Industry-specific subsidies',
        'Employment-linked incentives',
        'Export promotion schemes'
      ],
      image: 'https://images.pexels.com/photos/1407487/pexels-photo-1407487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      id: 'sme-loans',
      icon: <Building2 className="w-10 h-10" />,
      title: 'SME & Project Loans',
      subtitle: 'Business Financing Solutions',
      description: 'Raising finance is the most significant challenge entrepreneurs face in today\'s competitive landscape. We help clients secure adequate financing from various sources at optimal rates and favorable terms, supporting new projects, expansion, and working capital needs.',
      features: [
        'New Project financing',
        'Business expansion loans',
        'Working capital funding',
        'Equipment financing',
        'Term loans',
        'MUDRA loans',
        'CGTMSE-backed loans',
        'Cash credit facilities'
      ],
      image: 'https://images.unsplash.com/photo-1554290671-aa496cf17b4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsYXNzJTIwZ2VvbWV0cmljJTIwYXJjaGl0ZWN0dXJlJTIwdGVhbCUyMGJsdWV8ZW58MHx8fHwxNzcxMzkxMzU0fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'retail-loans',
      icon: <Wallet className="w-10 h-10" />,
      title: 'Retail Loans',
      subtitle: 'Personal & Business Financing',
      description: 'Through our extensive network of partner banks and NBFCs, we bring you the best deals in retail loans. Capital flow is the foundation of any successful business, and we ensure our customers get the best finance solutions to scale to new heights.',
      features: [
        'Home Loans',
        'Loan Against Property (LAP)',
        'Business Loans',
        'Personal Loans',
        'Vehicle financing',
        'Education loans',
        'Overdraft facilities',
        'Balance transfer options'
      ],
      image: 'https://images.pexels.com/photos/955394/pexels-photo-955394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  const loanTypes = [
    { icon: <Home className="w-6 h-6" />, name: 'Home Loans', description: 'Competitive rates for your dream home' },
    { icon: <Landmark className="w-6 h-6" />, name: 'Loan Against Property', description: 'Unlock the value of your assets' },
    { icon: <Briefcase className="w-6 h-6" />, name: 'Business Loans', description: 'Fuel your business growth' },
    { icon: <Factory className="w-6 h-6" />, name: 'Project Finance', description: 'Large-scale project funding' }
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-[#f8fafc]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-6 shadow-sm">
              Our Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#0f172a] leading-tight mb-6">
              Comprehensive Financial
              <span className="gradient-text block">Solutions</span>
            </h1>
            <p className="text-lg text-[#64748b] leading-relaxed">
              From government subsidies to business and retail loans, we provide 
              end-to-end consultancy services tailored to your unique requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-24 ${index % 2 === 1 ? 'bg-[#f8fafc]' : 'bg-white'}`}
          data-testid={`service-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mb-6">
                  {service.icon}
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-2">
                  {service.title}
                </h2>
                <p className="text-[#00bcd4] font-medium mb-6">{service.subtitle}</p>
                <p className="text-[#64748b] leading-relaxed mb-8">
                  {service.description}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#00bcd4] flex-shrink-0" />
                      <span className="text-[#0f172a] text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${service.id}`}
                  className="inline-flex items-center gap-2 gradient-btn px-8 py-4 rounded-full font-medium text-white mt-8"
                >
                  Get Consultation
                  <ArrowRight size={18} />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <div className="relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                  />
                  <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#3b5998] to-[#00bcd4] rounded-2xl opacity-20"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Loan Types Grid */}
      <section className="py-24 bg-[#0f172a]" data-testid="loan-types">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              Financing Options
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Diverse loan products to meet every financial requirement.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loanTypes.map((loan, index) => (
              <motion.div
                key={loan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mx-auto mb-4">
                      {loan.icon}
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">{loan.name}</h3>
                    <p className="text-slate-400 text-sm">{loan.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-[#f1f5f9] rounded-full text-sm font-medium text-[#3b5998] mb-4">
              Our Process
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              D2D Model — Documentation to Disbursement
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              A streamlined approach ensuring complete support throughout your financial journey.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Understanding your requirements and financial goals' },
              { step: '02', title: 'Documentation', description: 'Comprehensive preparation of all required documents' },
              { step: '03', title: 'Processing', description: 'Submission and follow-up with relevant authorities' },
              { step: '04', title: 'Disbursement', description: 'Successful fund release and post-sanction support' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-center">
                  <span className="stat-number text-5xl font-semibold mb-4 block">{item.step}</span>
                  <h3 className="font-heading text-xl font-semibold text-[#0f172a] mb-2">{item.title}</h3>
                  <p className="text-[#64748b] text-sm">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 right-0 translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-[#3b5998] to-[#00bcd4]"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#f8fafc]" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto mb-8">
              Schedule a consultation with our experts and discover the best financial solutions for your needs.
            </p>
            <Link
              to="/contact"
              data-testid="services-final-cta"
              className="inline-flex items-center gap-2 gradient-btn px-10 py-4 rounded-full font-medium text-white"
            >
              Contact Us Today
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
